import { PUBLIC_PAGE_DEFAULT_LOCALE, withLangParam } from '@/components/Functional/utils/publicPageLocale'

/**
 * v2 Auth サーバ（NEXT_PUBLIC_AUTH_V2_URL）向けのエンドポイントヘルパー。
 * httpOnly Cookie を使用するため、fetch 時に credentials: 'include' が必要。
 *
 * `lang` クエリを付けるのは画面遷移するエンドポイントだけ（#3504）。
 * check / tokenRefresh / logout は JSON を返す fetch で利用者に見える文言を持たないため、
 * 不要にセッションを開始させないよう付与しない。
 */
export const authV2Url = {
  /**
   * GET /{campaignSlug}/auth/check
   * 認証状態確認・IdToken 取得
   */
  check: (campaignSlug: string) => `${process.env.NEXT_PUBLIC_AUTH_V2_URL}/${campaignSlug}/auth/check`,
  /**
   * POST /{campaignSlug}/token/refresh
   * IdToken / AccessToken の更新（リクエストボディ不要・Cookie の RefreshToken を使用）
   */
  tokenRefresh: (campaignSlug: string) => `${process.env.NEXT_PUBLIC_AUTH_V2_URL}/${campaignSlug}/token/refresh`,
  /**
   * POST /{campaignSlug}/auth/logout
   * ログアウト（httpOnly Cookie 削除・セッション破棄）
   */
  logout: (campaignSlug: string) => `${process.env.NEXT_PUBLIC_AUTH_V2_URL}/${campaignSlug}/auth/logout`,
  /**
   * GET /{campaignSlug}/line/authorize/force-friend
   * LINE友達追加強制リダイレクト先（LINE未友達ユーザを誘導するための authサーバエンドポイント）
   *
   * `lang` を付けると、バックエンドがこのロケールをセッションに退避する（#3487）。
   * LINE の認可コールバックは redirect_uri の完全一致制約で `lang` を持って戻れないため、
   * ここで付けておかないと友だち追加の再試行画面が日本語に戻る。
   */
  forceFriendAdd: (campaignSlug: string, locale: string = PUBLIC_PAGE_DEFAULT_LOCALE) =>
    withLangParam(`${process.env.NEXT_PUBLIC_AUTH_V2_URL}/${campaignSlug}/line/authorize/force-friend`, locale),
  /**
   * GET /{campaignSlug}/account/password
   * ログイン済み会員のパスワード変更画面（#3605）。メニューの STATIC `ACCOUNT_PASSWORD` の遷移先（#3616）。
   *
   * 画面遷移するエンドポイントなので、ログイン画面・LINE 友だち追加と同じく `lang` を付ける（日本語は付けない）。
   * ログインしていない場合は auth 側がログイン画面へリダイレクトする。
   */
  accountPassword: (campaignSlug: string, locale: string = PUBLIC_PAGE_DEFAULT_LOCALE) =>
    withLangParam(`${process.env.NEXT_PUBLIC_AUTH_V2_URL}/${campaignSlug}/account/password`, locale),
  /**
   * GET /{campaignSlug}/reauth?return_to=…
   * 決済などの前に本人確認を求める再認証画面（#3610）。API が 403 REAUTH_REQUIRED を返したときの移動先。
   *
   * - `returnTo` は「その画面を開き直せる URL」（クエリ・ハッシュ込み）。auth 側は APPS_BASE_URL か
   *   キャンペーンの独自ドメインと一致する URL だけを受け付ける（ログイン画面の return_to と同じ規則）
   * - 画面遷移するエンドポイントなので、ログイン画面・LINE 友だち追加と同じく `lang` を付ける（日本語は付けない）
   * - `return_to` を先に付けてから withLangParam() に通す。withLangParam は既存クエリを文字列のまま温存するため、
   *   return_to のパーセントエンコーディングが崩れない
   */
  reauth: (campaignSlug: string, returnTo: string, locale: string = PUBLIC_PAGE_DEFAULT_LOCALE) =>
    withLangParam(
      `${process.env.NEXT_PUBLIC_AUTH_V2_URL}/${campaignSlug}/reauth?return_to=${encodeURIComponent(returnTo)}`,
      locale,
    ),
}
